import { z } from 'zod';

// --- Enums ---
export const FormTargetProfile = z.enum(['PREGNANT', 'TENTANTE', 'POSTPARTUM', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE', 'FATHER_FIGURE']);
/**
 * EMAIL e PHONE (A127) existem para o formulário validar e mascarar o que a
 * pessoa digita — antes eram texto livre. O enum só se alarga: formulário
 * gravado com os tipos antigos continua igual.
 */
export const FormFieldType = z.enum(['TEXT', 'NUMBER', 'SELECT', 'MULTISELECT', 'DATE', 'BOOLEAN', 'TERMS', 'SECTION', 'INFO', 'EMAIL', 'PHONE']);

/**
 * Como o BOOLEAN aparece (A127). Sem o campo, os dois botões Sim/Não de
 * sempre; `CAIXA` é uma caixa de marcar só com o rótulo, para afirmações
 * opcionais como "Autorizo mencionarem o meu nome". Desmarcada, a resposta
 * vai como `false`.
 */
export const FormatoDoBooleano = z.enum(['SIM_NAO', 'CAIXA']);

/** Limites do topo do formulário (A127): o chapéu é uma linha em caixa alta, o botão é uma pílula. */
export const CHAPEU_DO_FORMULARIO_MAX = 30;
export const TEXTO_DO_BOTAO_DO_FORMULARIO_MAX = 30;

// --- Form Fields ---
export const FormFieldSchema = z.object({
    id: z.string(), // key for storage/rendering
    type: FormFieldType,
    label: z.string(),
    placeholder: z.string().optional(),
    required: z.boolean().default(false),
    options: z.array(z.string()).optional(), // For select/multiselect
    systemFieldKey: z.string().optional(), // If mapping to existing User schema field
    termsFileUrl: z.string().optional(), // For TERMS field
    termsLabel: z.string().optional(), // For TERMS field (checkbox label)
    trueLabel: z.string().optional(), // For BOOLEAN switch
    falseLabel: z.string().optional(), // For BOOLEAN switch
    unit: z.string().optional(), // For NUMBER field
    formato: FormatoDoBooleano.optional(), // For BOOLEAN field (A127)
});

// --- Care Form Version ---
export const CareFormVersionSchema = z.object({
    version: z.number().int().positive(),
    fields: z.array(FormFieldSchema).default([]),
    createdAt: z.date(),
    createdBy: z.string().optional(),
});

// --- Care Form (Entity) ---
export const CareFormSchema = z.object({
    id: z.string().uuid(),
    partnerId: z.string().optional().nullable(), // Null = Eva/Global Form
    title: z.string(),
    description: z.string().optional(),
    /** Linha curta acima do título no topo do modal, como "INDIQUE A EVA" (A127). Nulo apaga. */
    chapeu: z.string().max(CHAPEU_DO_FORMULARIO_MAX).nullable().optional(),
    /** Texto do botão de enviar; sem ele, "Enviar" (A127). Nulo apaga. */
    textoDoBotao: z.string().max(TEXTO_DO_BOTAO_DO_FORMULARIO_MAX).nullable().optional(),
    versions: z.array(CareFormVersionSchema).default([]),

    // Audit
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable().default(null),
});

// --- Care Form Response ---
export const CareFormResponseSchema = z.object({
    id: z.string().uuid(),
    formId: z.string().uuid(),
    formVersion: z.number().int().positive(),
    userId: z.string(), // Patient ID
    /** Marca da sessão em que foi respondido (A052, fase 2/3). Opcional até o backfill. */
    brandId: z.string().nullable().optional(),

    // Answers: key = field.id, value = answer
    answers: z.record(z.any()),

    // Admin user IDs that have viewed this response (clicked the eye icon)
    viewedBy: z.array(z.string()).default([]),

    createdAt: z.date(),
});

// --- Types ---
export type FormTargetProfile = z.infer<typeof FormTargetProfile>;
export type FormFieldType = z.infer<typeof FormFieldType>;
export type FormatoDoBooleano = z.infer<typeof FormatoDoBooleano>;
export type FormField = z.infer<typeof FormFieldSchema>;
export type CareFormVersion = z.infer<typeof CareFormVersionSchema>;
export type CareForm = z.infer<typeof CareFormSchema>;
export type CareFormResponse = z.infer<typeof CareFormResponseSchema>;

export interface FormDefinition {
    id?: string;
    title: string;
    description?: string;
    chapeu?: string | null;
    textoDoBotao?: string | null;
    fields: FormField[];
}

/** E-mail com cara de e-mail: algo@dominio.tld, sem espaço. */
export function emailDeFormularioValido(valor: string | null | undefined): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(valor ?? '').trim());
}
